import { ActionIcon, Button, Group, Text } from '@mantine/core'
import { IconAlertTriangle, IconX } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import cx from 'clsx'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { Link } from 'wouter'

import { useGlobalState } from '@/hooks/useGlobalState'
import * as classes from '@/styles/CardExpiryBanner.css'
import { formatBillingDate } from '@/utils/analytics'
import { getRoutePath } from '@/utils/navigation'

const DISMISSED_STORAGE_KEY = 'cardExpiryBannerDismissed'

function readDismissed() {
  try {
    return sessionStorage.getItem(DISMISSED_STORAGE_KEY)
  } catch {
    return null
  }
}

export const CardExpiryBanner = observer(function CardExpiryBanner() {
  const { api, authenticationStore } = useGlobalState()
  const { isSponsorAdmin, user } = authenticationStore
  const [dismissed, setDismissed] = useState(readDismissed)

  // billing_info reads the card from Stripe, so the layout refetches it at most hourly.
  const billingInfoQuery = useQuery({
    queryKey: ['billing', 'info'],
    queryFn: () => api.getBillingInfo(),
    enabled: isSponsorAdmin,
    staleTime: 60 * 60 * 1000,
  })

  const billingInfo = billingInfoQuery.data?.billingInfo
  const cardExpiry = billingInfo?.cardExpiry
  if (!isSponsorAdmin || !billingInfo || !cardExpiry?.showNotice) return null

  // Keyed by status so dismissing "expires soon" doesn't also hide "has expired".
  const noticeKey = `${user?.userId}:${user?.sponsorId}:${cardExpiry.expiresOn}:${cardExpiry.status}`
  if (dismissed === noticeKey) return null

  const expired = cardExpiry.status === 'expired'
  const tone = expired ? 'red' : 'amber'
  const nextBillingDate = billingInfo.nextBillingDate ? formatBillingDate(billingInfo.nextBillingDate, true) : null
  const message = expired
    ? nextBillingDate
      ? `Your card has expired. Add a new card before your next billing date, ${nextBillingDate}.`
      : 'Your card has expired. Add a new card.'
    : `Your card on file expires on ${formatBillingDate(cardExpiry.expiresOn, true)}. Update it to keep Sidekick running for your users.`

  const dismiss = () => {
    setDismissed(noticeKey)
    try {
      sessionStorage.setItem(DISMISSED_STORAGE_KEY, noticeKey)
    } catch {
      // Without storage the dismissal still holds until the next reload.
    }
  }

  return (
    <section className={cx(classes.banner, classes.tone[tone])} aria-label="Card on file">
      <IconAlertTriangle size={20} stroke={1.5} aria-hidden="true" className={classes.icon[tone]} />
      <Text size="sm" fw={500} className={classes.text}>
        {message}
      </Text>
      <Group gap="xs" wrap="nowrap" className={classes.actions}>
        <Button
          component={Link}
          to={getRoutePath('billing', { searchParams: { tab: 'payment' } })}
          size="xs"
          color={expired ? 'red' : undefined}>
          Update card
        </Button>
        <ActionIcon variant="subtle" color="gray" aria-label="Dismiss" onClick={dismiss}>
          <IconX size={16} aria-hidden="true" />
        </ActionIcon>
      </Group>
    </section>
  )
})
